import { useState } from 'react';
import { apiClient, axiosClient } from '../../auth-lib';
import { Button, Modal, Select, Input, useToast } from '../../design-system';
import { useWorkload, workloadSuffix, workloadWarning } from '../../lib/workload';
import { useEffect } from 'react';
import '../styles/BulkActions.css';
import { formatTaskNumber } from '../../lib/taskRef';

// Actions applied to a multi-selection of tasks.
//
// Every endpoint behind this is PARTIAL-SUCCESS: it reports what happened to
// each task rather than failing the batch on the first problem. So this bar
// always summarises the outcome ("48 rescheduled, 2 skipped") instead of a
// single success/failure toast, and shows exactly which ones were skipped and
// why — a silent partial result is worse than an error.
function BulkActionBar({ selectedIds, tasks, onClear, onDone, canApprove, canEdit }) {
  const toast = useToast();
  const [orgUsers, setOrgUsers] = useState([]);
  const [openAction, setOpenAction] = useState(null); // 'assign' | 'reschedule'
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  // Assign form
  const [preparerId, setPreparerId] = useState('');
  const [reviewerId, setReviewerId] = useState('');

  // The 180h monthly baseline. Purely informational — see lib/workload.js.
  const workload = useWorkload();
  const preparerWarning = workloadWarning(workload.byUser.get(String(preparerId)), {
    capacityHours: workload.capacityHours,
  });
  const reviewerWarning = workloadWarning(workload.byUser.get(String(reviewerId)), {
    capacityHours: workload.capacityHours,
  });
  // Reschedule form
  const [mode, setMode] = useState('shift'); // 'shift' | 'absolute'
  const [shiftDays, setShiftDays] = useState('7');
  const [dueDate, setDueDate] = useState('');

  useEffect(() => {
    if (openAction === 'assign' && !orgUsers.length) {
      axiosClient.get('/users').then((res) => setOrgUsers(res.data)).catch(() => {});
    }
  }, [openAction, orgUsers.length]);

  const count = selectedIds.length;
  if (!count) return null;

  // How many of the selection are actually eligible for approval — the queue
  // and the task list share this bar, and only proposed tasks can be approved.
  const proposedCount = tasks.filter(
    (t) => selectedIds.includes(t._id) && t.approvalStatus === 'proposed'
  ).length;

  const summarise = (data, verb) => {
    const ok = data.succeeded.length;
    const bad = data.failed.length;
    if (bad === 0) toast.success(`${ok} task${ok === 1 ? '' : 's'} ${verb}`);
    else if (ok === 0) toast.danger(`Nothing ${verb} — ${bad} skipped`);
    else toast.info(`${ok} ${verb}, ${bad} skipped`);
    // Keep the detail on screen when anything was skipped, so "why didn't
    // those two move?" is answerable without guessing.
    setResult(bad > 0 ? data : null);
    if (bad === 0) {
      setOpenAction(null);
      onClear();
    }
    onDone();
  };

  const run = async (url, body, verb) => {
    setBusy(true);
    setResult(null);
    try {
      const res = await apiClient.post(url, { ids: selectedIds, ...body });
      summarise(res.data, verb);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Bulk action failed');
    } finally {
      setBusy(false);
    }
  };

  const approve = (action) => {
    if (action === 'reject' && !window.confirm(`Reject ${proposedCount} proposed task(s)?`)) return;
    run('/api/tasks/bulk/approve', { action }, action === 'approve' ? 'approved' : 'rejected');
  };

  const submitAssign = () => {
    if (!preparerId && !reviewerId) {
      toast.danger('Pick a preparer or a reviewer');
      return;
    }
    const body = {};
    // Only send what was chosen — an untouched field must stay untouched on
    // every task in the batch, not be cleared.
    if (preparerId) body.preparerId = preparerId;
    if (reviewerId) body.reviewerId = reviewerId;
    run('/api/tasks/bulk/assign', body, 'reassigned');
  };

  const submitReschedule = () => {
    if (mode === 'shift') {
      const n = Number(shiftDays);
      if (!n) {
        toast.danger('Enter a number of days to shift by');
        return;
      }
      run('/api/tasks/bulk/reschedule', { shiftDays: n }, 'rescheduled');
    } else {
      if (!dueDate) {
        toast.danger('Pick a due date');
        return;
      }
      run('/api/tasks/bulk/reschedule', { dueDate }, 'rescheduled');
    }
  };

  return (
    <>
      <div className="bulk-bar" role="region" aria-label="Bulk actions">
        <span className="bulk-count">
          <strong>{count}</strong> selected
        </span>

        <div className="bulk-actions">
          {canApprove && proposedCount > 0 && (
            <>
              <Button variant="primary" size="sm" onClick={() => approve('approve')} loading={busy}>
                Approve {proposedCount}
              </Button>
              <Button variant="secondary" size="sm" onClick={() => approve('reject')} disabled={busy}>
                Reject {proposedCount}
              </Button>
            </>
          )}
          {canEdit && (
            <>
              <Button variant="secondary" size="sm" onClick={() => setOpenAction('assign')} disabled={busy}>
                Reassign
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setOpenAction('reschedule')} disabled={busy}>
                Reschedule
              </Button>
            </>
          )}
        </div>

        <button type="button" className="bulk-clear" onClick={onClear}>Clear</button>
      </div>

      {/* What was skipped, and why. Shown only when something was. */}
      {result && (
        <div className="bulk-result" role="alert">
          <div className="bulk-result-head">
            <strong>{result.failed.length} task(s) skipped</strong>
            <button type="button" onClick={() => setResult(null)}>Dismiss</button>
          </div>
          <ul className="bulk-result-list">
            {result.failed.map((f) => (
              <li key={f.id}>
                {f.taskNumber != null ? formatTaskNumber(f.taskNumber) : f.id}
                {' — '}
                {f.error}
              </li>
            ))}
          </ul>
        </div>
      )}

      <Modal
        isOpen={openAction === 'assign'}
        onClose={() => setOpenAction(null)}
        title={`Reassign ${count} task(s)`}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpenAction(null)} disabled={busy}>Cancel</Button>
            <Button variant="primary" onClick={submitAssign} loading={busy}>Apply</Button>
          </>
        }
      >
        <p className="bulk-hint">
          Only the roles you choose are changed. Completed tasks are locked and will be skipped.
        </p>
        <Select label="Preparer" value={preparerId} onChange={(e) => setPreparerId(e.target.value)}>
          <option value="">— leave unchanged —</option>
          {/* Hours booked this month next to each name. Advisory: nobody is
              filtered out and nothing is disabled — the point is to decide
              with the number in view, not to be stopped by it. */}
          {orgUsers.map((u) => (
            <option key={u._id} value={u._id}>
              {u.name}{workloadSuffix(workload.byUser.get(String(u._id)), workload.capacityHours)}
            </option>
          ))}
        </Select>
        {preparerWarning && <p className="bulk-workload-warn">⚠ {preparerWarning}</p>}

        <Select label="Reviewer" value={reviewerId} onChange={(e) => setReviewerId(e.target.value)}>
          <option value="">— leave unchanged —</option>
          {orgUsers.map((u) => (
            <option key={u._id} value={u._id}>
              {u.name}{workloadSuffix(workload.byUser.get(String(u._id)), workload.capacityHours)}
            </option>
          ))}
        </Select>
        {reviewerWarning && <p className="bulk-workload-warn">⚠ {reviewerWarning}</p>}
      </Modal>

      <Modal
        isOpen={openAction === 'reschedule'}
        onClose={() => setOpenAction(null)}
        title={`Reschedule ${count} task(s)`}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpenAction(null)} disabled={busy}>Cancel</Button>
            <Button variant="primary" onClick={submitReschedule} loading={busy}>Apply</Button>
          </>
        }
      >
        <Select label="How" value={mode} onChange={(e) => setMode(e.target.value)}>
          {/* Shifting is the realistic quarter-end move — every task keeps its
              relative spacing. Setting one absolute date collapses them all
              onto the same day, which is rarely what's meant. */}
          <option value="shift">Shift existing dates by…</option>
          <option value="absolute">Set the same due date for all</option>
        </Select>

        {mode === 'shift' ? (
          <>
            <Input
              label="Days"
              type="number"
              value={shiftDays}
              onChange={(e) => setShiftDays(e.target.value)}
              helperText="Negative to pull dates earlier. Tasks with no due date are skipped."
            />
          </>
        ) : (
          <Input
            label="Due date"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />
        )}
      </Modal>
    </>
  );
}

export default BulkActionBar;
